import React, { useEffect, useMemo, useRef, useState } from "react";
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-alpine.css";
import FilterSection from "../../../components/FilterOrderSection";
import PaginationTable from "../../../components/Pagination";
import { Button, Divider, Modal, Steps } from "antd";
import { codeFormatter } from "../../../utils/formatters";
import api from "../../../utils/axios";
import { ColDef } from "ag-grid-community";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import OrderDetailRow from "../../../components/form/OrderDetailRow";
import { toast } from "react-toastify";
import { orderStatuses } from "../../../types/enums";
import useLang from "../../../hooks/useLang";

const getValueFromOption = (value: string) => {
	if (value?.toString().includes("-")) {
		return value.split("-")[0];
	}
	return value;
};

const ReturnOrders = () => {
	let defaultExcelExportSettings = {};
	const defaultColDef = useMemo(
		() => ({
			sortable: true,
			filter: true,
			wrapHeaderText: true,
			resizable: true,
			filterParams: { excelMode: "windows" },
			autoSizeAllColumns: true,
		}),
		[]
	);
	let grid = useRef<AgGridReact | null>(null);
	const [tableData, setTableData] = useState([]);
	const [tableColumns, setTableColumns] = useState<ColDef[]>();
	const [openModal, setOpenModal] = useState<boolean>();
	const [openOrderHistory, setOpenOrderHistory] = useState<boolean>();
	const [rowData, setRowData] = useState<any>();
	const [orderHistory, setOrderHistory] = useState<any[]>([]);
	const [filterData, setFilterData] = useState<any>({
		statusIds: [],
	});

	const hexToRgba = (hex: string, alpha: number) => {
		const r = parseInt(hex.slice(1, 3), 16);
		const g = parseInt(hex.slice(3, 5), 16);
		const b = parseInt(hex.slice(5, 7), 16);
		return `rgba(${r},${g},${b},${alpha})`;
	};

	const fetchOrderHistory = async (orderId: string) => {
		try {
			const response = await api.get("Order/short-order-history", {
				params: {
					orderId: orderId,
				},
			});
			const result = response.data.reverse();
			setOrderHistory(result);
		} catch (error) {
			console.log("Error:", error);
		}
	};

	const fetchTableData = async () => {
		getOrders(
			pageSize,
			currentPage,
			filterData?.countryIds,
			filterData?.cityIds,
			filterData?.statusIds,
			filterData?.displayId,
			filterData?.orderDescription,
			filterData?.fromData,
			filterData?.toDate,
			filterData?.filterType,
			filterData?.sellerIds,
			filterData?.staffIds,
			filterData?.selectedSettled,
			filterData?.orderTypeFilter
		);
	};

	const fetchOrders = async (
		pageSize: number,
		currentPage: number,
		countryIds?: any[],
		cityIds?: any[],
		statusIds?: any[],
		displayId?: any,
		orderDescription?: any,
		fromDate?: any,
		toDate?: any,
		filterType?: any,
		orderTypeFilter?: any,
		sellerIds?: any[],
		staffIds?: any[],
		selectedSettled?: boolean,
		paginationDisabled?: boolean
	) => {
		const displayIdSearchTerm = displayId || null;
		const response = await api.post(
			"Order/return-storage-orders-paginated",
			{
				pageSize,
				currentPage,
				countryIds:
					countryIds?.map((c) => ({
						id: getValueFromOption(c),
					})) || [],
				cityIds:
					cityIds?.map((c) => ({ id: getValueFromOption(c) })) || [],
				statusIds:
					statusIds?.map((c) => ({
						id: getValueFromOption(c),
					})) || [],
				displayId,
				orderDescription,
				fromDate,
				toDate,
				filterType,
				orderTypeFilter: orderTypeFilter || null,
				sellerIds:
					sellerIds?.map((c) => ({
						id: getValueFromOption(c),
					})) || [],
				staffIds:
					staffIds?.map((c) => ({ id: getValueFromOption(c) })) || [],
				settled: selectedSettled,
				paginationDisabled,
				displayIdSearchTerm,
			}
		);
		return response.data;
	};

	const getOrders = async (
		pageSize: number,
		currentPage: number,
		countryIds?: any[],
		cityIds?: any[],
		statusIds?: any[],
		displayId?: any,
		orderDescription?: any,
		fromDate?: any,
		toDate?: any,
		filterType?: any,
		orderTypeFilter?: any,
		sellerIds?: any[],
		staffIds?: any[],
		selectedSettled?: boolean,
		paginationDisabled?: boolean
	) => {
		try {
			const responseData = await fetchOrders(
				pageSize,
				currentPage,
				countryIds,
				cityIds,
				statusIds,
				displayId,
				orderDescription,
				fromDate,
				toDate,
				filterType,
				orderTypeFilter,
				sellerIds,
				staffIds,
				selectedSettled,
				paginationDisabled
			);
			setTableData(responseData?.data);
			setTotal(responseData?.total);
			return responseData;
		} catch (err) {
			console.log(err);
		}
	};
	const onFilterOrders = (
		countryIds: any[],
		cityIds: any[],
		statusIds: any[],
		displayIds: any[],
		orderDescription: any,
		fromDate: any,
		toDate: any,
		filterType: any,
		orderTypeFilter: any,
		sellerIds: any[],
		staffIds: any[],
		selectedSettled: boolean | undefined
	) => {
		setFilterData({
			countryIds,
			cityIds,
			statusIds,
			displayIds,
			orderDescription,
			fromDate,
			toDate,
			filterType,
			orderTypeFilter,
			sellerIds,
			staffIds,
			selectedSettled,
		});
		setCurrentPage(1);
		getOrders(
			pageSize,
			1,
			countryIds,
			cityIds,
			statusIds,
			displayIds,
			orderDescription,
			fromDate,
			toDate,
			filterType,
			orderTypeFilter,
			sellerIds,
			staffIds,
			selectedSettled
		);
	};

	useEffect(() => {
		fetchTableData();
		setTableColumns([
			{
				// type: "numericColumn",
				// filter: "agNumberColumnFilter",
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 38,
			},
			{
				field: "displayId",
				headerName: "Kodi",
				valueFormatter: (params) => codeFormatter(params.value),
				// type: "numericColumn",
				// filter: "agNumberColumnFilter",
				cellStyle: (params: any) => ({
					fontSize: "15px",
					color: "blue",
				}),
				width: 100,
			},
			{
				field: "statusDescription",
				headerName: "Statusi",
				width: 150,
				cellRendererFramework: (params: any) => {
					const statusColorHexCode =
						params.data.statusColorHexCode || "#ffff";
					const rgbaColor = hexToRgba(statusColorHexCode, 0.08);
					return (
						<div className="statusStyle">
							<div
								className="statusValues"
								style={{
									backgroundColor: rgbaColor,
									color: statusColorHexCode,
								}}
							>
								<p className="text-lg">&#x2022;</p>
								<p className="w-full">{params.value}</p>
							</div>
						</div>
					);
				},
			},
			{
				headerName: "Shitësi Username",
				cellRendererFramework: (params: any) => {
					const sellerUserName = params.data.sellerUserName;

					const sellerUniqueId = params.data.uniqueSellerIdentifier;
					const sellerString: string =
						sellerUserName + " (" + sellerUniqueId + ") ";
					return (
						<div className="buyerField">
							<p className="text-[#2C2C2C] font-semibold">
								{sellerString}
							</p>
						</div>
					);
				},
			},
			{
				headerName: "Shitësi",
				field: "sellerNameAndSurname",
				cellRendererFramework: (params: any) => {
					const sellerNumber = params.data.sellerPhoneNo;
					const sellerFullName = params.data.sellerNameAndSurname;
					return (
						<div className="buyerField">
							<p className="text-[#2C2C2C] font-semibold">
								{sellerFullName}
							</p>
							<p className="text-[#4F16C2] font-normal">
								{sellerNumber}
							</p>
						</div>
					);
				},
				width: 130,
			},
			{
				headerName: "Emri Biznesit",
				field: "businessName",
				width: 120,
			},
			{
				headerName: "Blerësi",
				cellRendererFramework: (params: any) => {
					const buyerNameSurname = params.data.buyerNameAndSurname;
					const phone = params.data.buyerPhoneNo;
					return (
						<div className="buyerField">
							<p className="text-[#2C2C2C] font-semibold">
								{buyerNameSurname}
							</p>
							<p className="text-[#4F16C2] font-normal">
								{phone}
							</p>
						</div>
					);
				},
			},
			{
				field: "orderDescription",
				headerName: "Përshkrimi",
			},
			{ field: "packageDescription", headerName: "Emërtimi", width: 150 },
			{
				field: "country",
				headerName: "Shteti",
				width: 120,
			},
			{
				field: "city",
				headerName: "Qyteti",
				width: 120,
			},

			{
				field: "orderPrice",
				headerName: "Vlera e pakos",
				minWidth: 150,
				maxWidth: 150,
			},
			{
				field: "modal",
				headerName: " ",
				width: 250,
				cellRenderer: (params: any) => {
					return (
						<Button size="large" style={{ color: "green" }}>
							Hap
						</Button>
					);
				},
			},
		]);
	}, [setTableColumns]);

	const onCellClicked = async (params: any) => {
		const colId = params.column.colId;
		switch (colId) {
			case "displayId":
				await onOrderIdClicked(params?.data);
				break;
			case "modal":
				await onModalOpen(params?.data);
				break;
		}
	};

	const onOrderIdClicked = (data: any) => {
		const orderId = data?.id?.toString();
		const url = `/order-details?id=${orderId}`;
		window.open(url, "_blank");
	};

	const onModalOpen = async (data: any) => {
		await fetchOrderHistory(data?.id);
		setRowData(data);
		setOpenModal(true);
	};
	const closeDetailsModal = () => {
		setOpenOrderHistory(false);
		setOpenModal(false);
	};

	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(20);
	const [total, setTotal] = useState(0);
	const onPageChanged = (page: number, pageSize: number) => {
		setCurrentPage(page);
		getOrders(
			pageSize,
			page,
			filterData?.countryIds,
			filterData?.cityIds,
			filterData?.statusIds,
			filterData?.displayIds,
			filterData?.orderDescription,
			filterData?.fromDate,
			filterData?.toDate,
			filterData?.filterType,
			filterData?.orderTypeFilter,
			filterData?.sellerIds,
			filterData?.staffIds,
			filterData?.selectedSettled
		);
	};
	const handlePageSizeChange = async (current: number, size: number) => {
		setPageSize(size);
		await getOrders(
			size,
			current,
			filterData?.countryIds,
			filterData?.cityIds,
			filterData?.statusIds,
			filterData?.displayIds,
			filterData?.orderDescription,
			filterData?.fromDate,
			filterData?.toDate,
			filterData?.filterType,
			filterData?.orderTypeFilter,
			filterData?.sellerIds,
			filterData?.staffIds,
			filterData?.selectedSettled
		);
	};
	const startOrderIndex = (currentPage - 1) * pageSize + 1;
	const endOrderIndex =
		startOrderIndex + Math.min(pageSize, total - startOrderIndex);

	const acceptOrderInWarehouse = async () => {
		const orderData = [];
		if (!rowData) {
			toast.error("Error!");
			return;
		}
		orderData.push({
			orderId: rowData?.id,
			statusId: rowData?.statusId,
			uuid: rowData?.uuid,
			originCountryId: rowData?.originCountryId,
		});
		try {
			const response = await api.post(
				"Order/return-storage-status-change",
				orderData
			);
			if (response.status === 200) {
				toast.success("Porosia u pranua me sukses ne depo!");
				await fetchOrderHistory(rowData.id);
				await fetchTableData();
				setOpenModal(false);
				setOpenOrderHistory(false);
				return;
			}
		} catch (error) {
			console.log("Error: " + error);
			toast.error("An error occurred");
		}
	};

	const printTickets = async (format: string) => {
		const ids = (grid.current as any).api
			.getSelectedRows()
			?.map((item: any) => ({
				id: item.displayId,
			}));

		if (!ids?.length) {
			toast.warning("Selekto porosite per te printuar");
			return true;
		}
		// const formatA4 = format === "A4";
		const params =
			format === "A4"
				? { formatA4: true }
				: format === "harleyTicket"
				? { harleyTicket: true }
				: {};

		try {
			const response = await api.post("Order/print-order-ticket", ids, {
				params,
				responseType: "blob",
			});
			const file = response.data;
			const fileUrl = window.URL.createObjectURL(file);

			const pdfWindow = window.open();
			if (pdfWindow) {
				pdfWindow.location.href = fileUrl;
			} else {
				toast.error(
					"Popup eshte i bllokuar. Ju lutemi lejoni popup-ët për këtë faqe."
				);
			}
		} catch (error) {
			console.log(error);
		}
	};

	const customOptions = [
		{
			label: "Printo tiketat",
			value: "print_ticket",
		},
		{
			label: "Printo listen",
			value: "print_list",
		},
	];

	const printOrderList = async () => {
		const ids = grid?.current?.api.getSelectedRows()?.map((item: any) => {
			return { id: item.id };
		});

		if (!ids || ids.length === 0) {
			toast.warning("No orders selected for printing");
			return;
		}

		try {
			const response = await api.post("Order/order-list-details", ids, {
				responseType: "blob",
			});

			const file = response.data;
			const fileUrl = window.URL.createObjectURL(file);

			const pdfWindow = window.open();
			if (pdfWindow) {
				pdfWindow.location.href = fileUrl;
			} else {
				toast.error(
					"Popup is blocked. Please allow pop-ups for this page."
				);
			}
		} catch (error) {
			console.error("Error exporting order list details:", error);
		}
	};

	return (
		<>
			<div className="orderMainContainer">
				<div className="ordersContainer">
					<FilterSection
						title="Te gjitha porosite"
						onFilterChange={onFilterOrders}
						selectedOptions={customOptions}
						showAdminFilters={true}
						onPrintList={printOrderList}
						onPrintTickets={printTickets}
					/>
					<PaginationTable
						current={currentPage}
						pageSize={pageSize}
						total={total}
						startOrderIndex={startOrderIndex}
						endOrderIndex={endOrderIndex}
						onChange={onPageChanged}
						onShowSizeChange={handlePageSizeChange}
					/>
					<div
						className="ag-theme-alpine"
						style={{
							width: "100%",
							height: "auto",
						}}
					>
						<AgGridReact
							rowData={tableData}
							columnDefs={tableColumns}
							defaultColDef={defaultColDef}
							undoRedoCellEditing={true}
							undoRedoCellEditingLimit={20}
							suppressCopyRowsToClipboard={true}
							defaultExcelExportParams={
								defaultExcelExportSettings
							}
							rowSelection="multiple"
							ref={grid}
							enableRangeSelection={true}
							cacheQuickFilter={true}
							domLayout="autoHeight"
							onCellClicked={onCellClicked}
							suppressPaginationPanel={false}
							rowHeight={60}
						/>
					</div>
				</div>
			</div>
			<Modal
				open={openModal}
				onCancel={closeDetailsModal}
				title={"Detajet e porosise"}
				cancelButtonProps={{ style: { display: "none" } }}
				okButtonProps={{ style: { display: "none" } }}
				footer={() => (
					<>
						<div className="flex items-end justify-end">
							<Button
								size="large"
								icon={<CloseOutlined />}
								onClick={() => setOpenModal(false)}
							>
								Mbyll
							</Button>
							{(rowData?.statusId === orderStatuses.REJECTED ||
								rowData?.statusId ===
									orderStatuses.RETURN_STORAGE ||
								rowData?.statusId ===
									orderStatuses.EXCHANGE) && (
								<Button
									type="primary"
									size="large"
									// className="bg-blue-600"
									style={{ backgroundColor: "#4F16C2" }}
									onClick={acceptOrderInWarehouse}
									icon={<CheckOutlined />}
								>
									{rowData.statusId ===
										orderStatuses.REJECTED ||
									rowData?.statusId === orderStatuses.EXCHANGE
										? "Prano ne depo"
										: "Në pritje të tërheqjes"}
								</Button>
							)}
						</div>
					</>
				)}
			>
				<Divider />
				<div className="flex flex-col rounded-lg border-2 border-slate-100">
					<OrderDetailRow
						label="Data e regjistrimit"
						// value={registeredDate}
						value={rowData?.createdAt}
						style={style.oddRow}
						className="driverDetailRow"
					/>
					<OrderDetailRow
						label="Statusi"
						value={rowData?.statusDescription}
						className="driverDetailRow"
					/>
					<OrderDetailRow
						label="Data e refuzimit"
						value={rowData?.refusedDate}
						style={style.oddRow}
						className="driverDetailRow"
					/>
					<OrderDetailRow
						label="Vlera e pakos"
						style={style.oddRow}
						value={rowData?.orderPrice.toFixed(2)}
						className="driverDetailRow"
					/>
					<OrderDetailRow
						label="Shitesi"
						value={`${rowData?.sellerNameAndSurname}, ${rowData?.sellerCity},${rowData?.sellerCountry}, ${rowData?.sellerPhoneNo} `}
						className="driverDetailRow"
					/>
				</div>
				<Divider />
				<div
					onClick={() => setOpenOrderHistory(!openOrderHistory)}
					className="flex flex-row gap-2"
				>
					{openOrderHistory ? <p>{"▼"}</p> : <p>{"▶"}</p>}

					<p> Historiku i porosise</p>
				</div>
				{openOrderHistory && (
					<Steps
						progressDot
						current={10}
						direction="vertical"
						items={orderHistory}
					/>
				)}
			</Modal>
		</>
	);
};

export default ReturnOrders;

const style = {
	oddRow: { backgroundColor: "#F9F8FC" },
};
